import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback } from "react";
import PublishModal from "../../../../components/PublishModal/PublishModal";
import { useToast } from "../../../../ui/Toast/ToastContext";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import Button from "../../../../ui/Button/Button";
import styles from "./PublishArticleModal.module.scss";

const PublishArticleModalQuery = graphql(/* GraphQL */ `
  query publishArticleModal($id: String!) {
    articleCounts(input: { filters: [{ ids: [$id] }] }) {
      published
      total
    }
    canPublish {
      canPublish
    }
  }
`);

const PublishArticleMutation = graphql(/* GraphQL */ `
  mutation publishArticle($id: String!) {
    publishArticle(id: $id) {
      __typename
      ... on PublishArticleResponseSuccess {
        article {
          id
          status
        }
      }

      ... on PublishArticleResponseError {
        errors {
          message
        }
      }
    }
  }
`);

function PublishArticleModal({
  isOpen,
  onClose,
  id,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
}) {
  const [publishArticleModalResponse] = useQuery({
    query: PublishArticleModalQuery,
    pause: !isOpen,
    variables: {
      id,
    },
  });
  const { showToast } = useToast();
  const [{ fetching, data }, publishArticle] = useMutation(
    PublishArticleMutation,
  );
  const [publishingErrorModalOpened, publishingErrorModal] = useDisclosure();

  const onSubmit = useCallback(async () => {
    const response = await publishArticle({
      id,
    });

    if (
      !response.error &&
      response.data?.publishArticle.__typename ===
        "PublishArticleResponseSuccess"
    ) {
      showToast({ title: "Article published" });
    } else if (
      response.data?.publishArticle.__typename === "PublishArticleResponseError"
    ) {
      showToast({
        variant: "error",
        title: "Publishing failed",
        description:
          response.data?.publishArticle.errors?.length === 1 ? (
            response.data?.publishArticle.errors[0].message
          ) : (
            <Button
              onClick={publishingErrorModal.open}
              className={styles.errorButton}
            >
              View errors
            </Button>
          ),
      });
    }
    onClose();
  }, [publishArticle, id, onClose, showToast, publishingErrorModal.open]);

  const canPublish = !!publishArticleModalResponse.data?.canPublish.canPublish;
  const articleCounts = publishArticleModalResponse.data?.articleCounts;
  const title = !canPublish
    ? `Connect your website to publish`
    : `Confirm ${
        articleCounts?.published === articleCounts?.total
          ? "republishing"
          : "publishing"
      } of article`;
  const subtitle =
    articleCounts?.published === articleCounts?.total
      ? "This will replace the article on your site"
      : undefined;

  return (
    <>
      <Modal
        isOpen={publishingErrorModalOpened}
        onClose={publishingErrorModal.close}
        title="Publishing failed"
      >
        <ul className={styles.errorList}>
          {!!data &&
            "errors" in data.publishArticle &&
            data.publishArticle.errors?.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
        </ul>
      </Modal>
      <Modal
        loading={
          publishArticleModalResponse.fetching ||
          !publishArticleModalResponse.data
        }
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
      >
        <PublishModal
          onClose={onClose}
          canPublish={canPublish}
          onSubmit={onSubmit}
          fetching={fetching || !isOpen}
          articleCounts={articleCounts}
        />
      </Modal>
    </>
  );
}

export default PublishArticleModal;
